import cx from 'classnames';
import { sortBy } from 'lodash-es';
import { useEffect, useState } from 'react';
import { Checkbox } from '@components/checkbox/Checkbox';
import { warningToast } from '@components/toasts/Toasts';
import { ApiUnauthorizedError } from '@shared/api/errors';
import { useError } from '@shared/hooks/useError';
import { reportAppError } from '@shared/reportAppError';
import { useUserContext } from 'webReservations/context/UserContext';
import typography from '~styles/typography.scss';
import {
  deleteGuestMarketingOptIn,
  getGuestMarketingOptIns,
  type RestaurantMarketingOptIn,
} from './apiHelpers';
import styles from './MarketingPreferences.scss';

export const MarketingPreferences = () => {
  const { clearUserState } = useUserContext();
  const setError = useError();

  const [optIns, setOptIns] = useState<RestaurantMarketingOptIn[] | null>(null);
  const [pendingRestaurantId, setPendingRestaurantId] = useState<string | null>(
    null,
  );

  const handleGetOptIns = async () => {
    try {
      const response = await getGuestMarketingOptIns();
      setOptIns(sortBy(response, 'restaurantName'));
    } catch (e) {
      if (e instanceof ApiUnauthorizedError) {
        clearUserState();
        return;
      }
      setError(e);
    }
  };

  useEffect(() => {
    handleGetOptIns();
  }, []);

  const handleOptOut = async (restaurantId: string) => {
    try {
      setPendingRestaurantId(restaurantId);
      await deleteGuestMarketingOptIn(restaurantId);
      setOptIns(
        (prev) =>
          prev?.filter((optIn) => optIn.restaurantId !== restaurantId) ?? null,
      );
    } catch (error) {
      reportAppError(error);
      warningToast({
        message: 'Error: Preference change not saved. Please try again.',
      });
    } finally {
      setPendingRestaurantId(null);
    }
  };

  if (!optIns || optIns.length === 0) return null;

  return (
    <div className={styles.card}>
      <h2 className={cx(styles.title, typography.h4)}>Marketing Preferences</h2>
      <fieldset className={styles.fieldset}>
        <legend>
          Modify which restaurants you receive marketing communications from.
        </legend>
        {optIns.map(({ restaurantId, restaurantName }) => (
          <div className={styles.optInRow} key={restaurantId}>
            <Checkbox
              className={styles.checkbox}
              checked
              disabled={pendingRestaurantId === restaurantId}
              label={restaurantName}
              onChange={() => handleOptOut(restaurantId)}
            />
          </div>
        ))}
      </fieldset>
    </div>
  );
};
